import { action, computed, makeObservable, observable } from "mobx"

import { DEFAULT_SLIPPAGE } from "@/domains/common/config/slippage"

import type { ManualClPositionRepository } from "../positions/manual-cl-position-repository"
import type { ManualClPositionStore } from "../positions/manual-cl-position-store"

const DEFAULT_DEADLINE_SECONDS = 20 * 60
const MIN_DEADLINE_SECONDS = 60

/**
 * Resolution status for the currently active position tokenId.
 *
 *   - `idle`     — no tokenId has been set yet (e.g. router hasn't mounted)
 *   - `loading`  — a tokenId is set but the repository hasn't hydrated yet
 *   - `found`    — the repository has hydrated and the position exists
 *   - `missing`  — the repository has hydrated, the post-create indexing
 *                  window has elapsed, and no position matches the id
 *
 * Manage views branch on this computed to render Loading / NotFound / content
 * without ever reaching into the router themselves.
 */
export type ResolvedPositionStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; position: ManualClPositionStore }
  | { kind: "missing"; tokenId: string }

/**
 * Shared state + action surface for the four manage-position stores
 * (increase, decrease, collect-fees, burn).
 *
 * The contract is deliberately narrow:
 *
 *   - A manage store NEVER fetches its own position by id. The repository is
 *     the single source of truth. When a tokenId changes, the store simply
 *     re-reads `repository.find(activeTokenId)` through `resolvedStatus`.
 *   - Slippage and deadline live on the base so the four subclasses share
 *     transaction settings via a single popover (see t33).
 *   - Subclasses MUST call `super.reset()` from their own `reset()` override
 *     so shared state is cleared between transactions.
 */
export abstract class ManagePositionStoreBase {
  activeTokenId: string | undefined = undefined

  slippage: number = DEFAULT_SLIPPAGE
  deadline: number = nowSeconds() + DEFAULT_DEADLINE_SECONDS
  deadlineDurationSeconds: number = DEFAULT_DEADLINE_SECONDS

  constructor(protected readonly positionRepository: ManualClPositionRepository) {
    makeObservable(this, {
      activeTokenId: observable,
      slippage: observable,
      deadline: observable,
      deadlineDurationSeconds: observable,

      resolvedStatus: computed,
      position: computed,
      activeNfpmTokenId: computed,
      deadlineMinutes: computed,

      setActiveTokenId: action,
      setSlippage: action,
      setDeadline: action,
      reset: action,
    })
  }

  /**
   * Idempotent write: calling with the same id (including undefined) is a
   * no-op so repeated route-change emissions don't re-trigger re-resolution
   * side-effects in subclasses.
   */
  setActiveTokenId(tokenId: string | undefined): void {
    if (this.activeTokenId === tokenId) return
    this.activeTokenId = tokenId
  }

  setSlippage(slippage: number): void {
    this.slippage = slippage
  }

  setDeadline(deadline: number): void {
    this.deadline = deadline
    this.deadlineDurationSeconds = Math.max(
      MIN_DEADLINE_SECONDS,
      Math.floor(deadline - nowSeconds()),
    )
  }

  protected getSubmitDeadline(): number {
    return nowSeconds() + this.deadlineDurationSeconds
  }

  get deadlineMinutes(): number {
    return Math.max(1, Math.round(this.deadlineDurationSeconds / 60))
  }

  /**
   * Reset shared transaction settings to defaults. Subclasses SHOULD call
   * `super.reset()` when clearing their own per-transaction state after a
   * successful submission or when the user navigates away.
   */
  reset(): void {
    this.slippage = DEFAULT_SLIPPAGE
    this.deadlineDurationSeconds = DEFAULT_DEADLINE_SECONDS
    this.deadline = nowSeconds() + DEFAULT_DEADLINE_SECONDS
  }

  /**
   * The resolved position store for `activeTokenId`, or `undefined` when the
   * id is missing, the repository hasn't hydrated, or no position matches.
   * Views should prefer `resolvedStatus` so they can render Loading vs
   * NotFound distinctly.
   */
  get position(): ManualClPositionStore | undefined {
    const status = this.resolvedStatus
    return status.kind === "found" ? status.position : undefined
  }

  /**
   * Real on-chain NFPM token id for the active position, if discovered.
   * Keep action stores on this value for contract calls; `activeTokenId`
   * can be a synthetic routing key before discovery completes.
   */
  get activeNfpmTokenId(): bigint | undefined {
    return this.position?.nfpmTokenId
  }

  get resolvedStatus(): ResolvedPositionStatus {
    if (this.activeTokenId === undefined) return { kind: "idle" }
    if (!this.positionRepository.isHydrated) return { kind: "loading" }
    const found = this.positionRepository.find(this.activeTokenId)
    if (found) return { kind: "found", position: found }
    if (this.positionRepository.isPostCreatePending(this.activeTokenId)) {
      return { kind: "loading" }
    }
    return { kind: "missing", tokenId: this.activeTokenId }
  }
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}
